// Hunt analyser: expected kills, XP and coins per hour for a lead creature at a hunt.
// A ranking aid, not a promise: it ignores swaps, potions, wipes and boosts.
import { attackInterval, basicMove, beatMs, blowBase, kitMoves, moveCooldownMs, moveScore, wildBlow, wildStats, ROLL_LO, CRIT, type Fighter } from './combat.ts';
import type { Rng } from './rng.ts';
import type { Rolls } from './rolls.ts';
import { computeStats } from './stats.ts';
import { wildStatSpecies } from './species.ts';
import { killXp, killCoins, LOOT_SHARE } from './xp.ts';
import { GROUNDS, groundUnlocked, type GroundDef } from './grounds.ts';

const AVG: Rng = { chance: () => false, range: (a: number, b: number) => (a + b) / 2 } as unknown as Rng;
const AVG_ROLLS: Rolls = { ivs: [15, 15, 15, 15, 15, 15], quality: 1, bloodline: 0, aura: false, trait: 'sturdy' };
/** A better-than-average wild (about the top third per stat): auto-move plans for these, not for the average. */
/** Wild hits land crits (CRIT.pve): 6% at ×1.5 is about 3% more damage on average. */
const CRIT_SHARE = 1 + CRIT.pve.chance * (CRIT.pve.mult - 1);
const TOUGH_ROLLS: Rolls = { ivs: [20, 20, 20, 20, 20, 20], quality: 1.05, bloodline: 0, aura: false, trait: 'sturdy' };
/** Average time from a kill to the next first blow: walking, spawns, wind-up (measured on the real hunt loop). */
export const WALK_SEC = 1.9;

export interface HuntEstimate { killsPerHour: number; xpPerHour: number; coinsPerHour: number; secondsPerKill: number; risk: 'safe' | 'ok' | 'risky'; mult: number }

/**
 * The lead's average blow on a wild, kit included: the basic attack at the middle roll (no crit), plus each move
 * the auto-cast would pick over the basic, weighted by how often it is ready (one beat in cooldown ÷ beat). The
 * hunt chart and the level edge are in blowBase, so this is the same formula the hunt fights with.
 */
export function avgBlow(lead: Fighter, wild: Fighter): number {
  const mid = (1 + ROLL_LO) / 2;
  const basic = basicMove(lead);
  const b = blowBase(lead, wild, basic, 'hunt').raw * mid;
  const bs = moveScore(lead, wild, basic, 'hunt');
  const beat = beatMs(lead);
  let extra = 0;
  for (const m of kitMoves(lead)) {
    if (moveScore(lead, wild, m, 'hunt') <= bs) continue;
    extra += (blowBase(lead, wild, m, 'hunt').raw * mid - b) * Math.min(1, beat / moveCooldownMs(lead, m));
  }
  return Math.max(1, b + extra);
}

/** A wild's average blow on the lead (base chart, its level edge on the lead). */
function wildAvg(wild: Fighter, lead: Fighter): number {
  return wildBlow(wild, lead, AVG).dmg;
}

/** An average wild of a hunt, with the stats it really fights with (wildStatSpecies). */
export function wildAt(g: GroundDef, rolls: Rolls = AVG_ROLLS): Fighter {
  const id = wildStatSpecies(g.species, g.level);
  return { speciesId: g.species, level: g.level, trait: 'sturdy', stats: wildStats(id, computeStats(id, g.level, rolls)) };
}

export function analyse(lead: Fighter, g: GroundDef): HuntEstimate {
  const level = g.level;
  const wild = wildAt(g);
  const hits = Math.ceil(wild.stats.hp / avgBlow(lead, wild));
  const fightSec = ((hits - 1) * beatMs(lead)) / 1000;
  const secondsPerKill = fightSec + WALK_SEC;
  const killsPerHour = 3600 / secondsPerKill;
  const taken = (wildAvg(wild, lead) * fightSec * 1000) / attackInterval(wild);
  const risk = taken < lead.stats.hp * 0.1 ? 'safe' : taken < lead.stats.hp * 0.3 ? 'ok' : 'risky';
  return {
    killsPerHour: Math.round(killsPerHour),
    xpPerHour: Math.round(killsPerHour * killXp(level)),
    // Kill coins plus the average sale value of loot.
    coinsPerHour: Math.round(killsPerHour * killCoins(level) * (1 + LOOT_SHARE)),
    secondsPerKill: Math.round(secondsPerKill * 10) / 10,
    risk,
    mult: blowBase(lead, wild, basicMove(lead), 'hunt').mult,
  };
}

/** Auto-move climbs only to a hunt the team can hold this long from its current HP and potions. */
export const HOLD_CLIMB_H = 1.5;
/** Auto-move leaves the current hunt when the team can no longer hold it this long. */
export const HOLD_STAY_H = 0.75;
/** Share of max HP one potion is counted for: it is drunk below 40%, so a big flask partly overheals. */
const POTION_HEAL_CAP = 0.6;

/** A team member as auto-move sees it: how it fights and the HP it has left. */
export interface HoldMember { fighter: Fighter; hp: number }

/**
 * HP a fighter loses per kill on a hunt, and the seconds a kill takes, against
 * a better-than-average wild so the estimate errs on the side of caution. Wild attacks
 * are counted one by one: the wild strikes 300 ms after the first blow, then
 * every attack interval until it falls.
 */
export function wear(f: Fighter, g: GroundDef): { taken: number; secondsPerKill: number } {
  const wild = wildAt(g, TOUGH_ROLLS);
  const hits = Math.ceil(wild.stats.hp / avgBlow(f, wild));
  const fightMs = (hits - 1) * beatMs(f);
  const strikes = fightMs > 300 ? Math.floor((fightMs - 301) / attackInterval(wild)) + 1 : 0;
  return { taken: strikes * wildAvg(wild, f) * CRIT_SHARE, secondsPerKill: fightMs / 1000 + WALK_SEC };
}

/**
 * Hours the team can hunt here before it is knocked out: each member still
 * standing fights until its HP is gone, then the next steps in. Potions on
 * hand top up the lead (the first member standing), each counted at most
 * POTION_HEAL_CAP of its max HP. HP does not come back on its own, so a hunt
 * that takes a bite on every kill wears the team down. Infinity when a member
 * standing takes no damage there.
 */
export function holdHours(team: readonly HoldMember[], potionHeal: number, g: GroundDef): number {
  let sec = 0, lead = true;
  for (const m of team) {
    if (m.hp <= 0) continue;
    const w = wear(m.fighter, g);
    const hp = m.hp + (lead ? potionHeal * m.fighter.stats.hp : 0);
    lead = false;
    sec += w.taken > 0 ? (hp / w.taken) * w.secondsPerKill : Infinity;
  }
  return sec / 3600;
}

/** Potion healing on hand, in shares of a max-HP bar (each counted at most POTION_HEAL_CAP). */
export function potionHeal(bag: Record<string, number>, heals: Record<string, number>): number {
  let h = 0;
  for (const [id, heal] of Object.entries(heals)) h += (bag[id] ?? 0) * Math.min(heal, POTION_HEAL_CAP);
  return h;
}

export interface MoveLimits {
  /** Team with current HP; when given, a hunt must also be holdable (see holdHours). */
  team?: readonly HoldMember[];
  /** Potion healing on hand for the lead, from potionHeal (0 when auto-potion is off). */
  potionHeal?: number;
  /** Skip hunts at or above this level (backing off after a knockout or retreat). */
  belowLevel?: number;
}

/**
 * Auto-move's pick: the best hunt for XP or coins per hour among the open
 * hunts of the current region that the analyser rates Safe for this lead.
 * Never another region, never Some risk or Risky. `avoid` holds hunts to skip
 * (ones the team was just knocked out of). Returns null when nothing beats
 * staying: the current hunt is kept unless it isn't Safe, or a Safe hunt is
 * at least 5% better.
 *
 * With `limits.team`, Safe also means the team can hold the hunt: climbing
 * needs HOLD_CLIMB_H hours of HP and potions, staying needs HOLD_STAY_H. The
 * gap between the two keeps it from bouncing. When the current hunt can't be
 * held and no hunt passes, it moves to the one the team holds longest.
 */
export function bestSafeHunt(lead: Fighter, current: GroundDef, trainerLevel: number, by: 'xp' | 'coins', avoid: ReadonlySet<string> = new Set(), limits: MoveLimits = {}): GroundDef | null {
  const rate = (e: HuntEstimate) => (by === 'xp' ? e.xpPerHour : e.coinsPerHour);
  const hold = (g: GroundDef) => (limits.team ? holdHours(limits.team, limits.potionHeal ?? 0, g) : Infinity);
  const open = (g: GroundDef) => g.region === current.region && groundUnlocked(g, trainerLevel) && !avoid.has(g.id) && g.level < (limits.belowLevel ?? Infinity);
  let best: GroundDef | null = null, bestRate = 0;
  let gentlest: GroundDef | null = null, gentlestHold = -1, gentlestRate = 0;
  for (const g of GROUNDS) {
    if (!open(g)) continue;
    const e = analyse(lead, g);
    const h = hold(g);
    if (h > gentlestHold || (h === gentlestHold && rate(e) > gentlestRate)) { gentlest = g; gentlestHold = h; gentlestRate = rate(e); }
    if (e.risk !== 'safe' || h < (g.id === current.id ? HOLD_STAY_H : HOLD_CLIMB_H)) continue;
    if (rate(e) > bestRate) { best = g; bestRate = rate(e); }
  }
  const here = analyse(lead, current);
  const hereOk = here.risk === 'safe' && open(current) && hold(current) >= HOLD_STAY_H;
  if (!best) return hereOk || !gentlest || gentlest.id === current.id || gentlestHold <= hold(current) ? null : gentlest;
  if (best.id === current.id) return null;
  if (hereOk && bestRate < rate(here) * 1.05) return null;
  return best;
}
